import { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { d202Api, FD120, ApprovalEvent } from '../../../services/d202Api';

interface UseD202ApprovalOptions {
  record: FD120 | null;
  isEditing: boolean;
  loadRecordWithNames: (receiptNo: string) => Promise<void>;
}

export function useD202Approval({ record, isEditing, loadRecordWithNames }: UseD202ApprovalOptions) {
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<ApprovalEvent[]>([]);

  const handleSubmitClick = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將繳庫單 ${record.fd120_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d202Api.submitForApproval(record.fd120_01);
          message.success(resp.message || '送審成功');
          await loadRecordWithNames(record.fd120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleApproveClick = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准繳庫單 ${record.fd120_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d202Api.approve(record.fd120_01);
          message.success(resp.message || '核准成功');
          await loadRecordWithNames(record.fd120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleRejectClick = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回繳庫單 ${record.fd120_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d202Api.reject(record.fd120_01);
          message.success(resp.message || '退回成功');
          await loadRecordWithNames(record.fd120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleShowHistory = useCallback(async () => {
    if (!record?.fd120_01) return;
    setHistoryVisible(true);
    setHistoryLoading(true);
    try {
      const events = await d202Api.getApprovalHistory(record.fd120_01);
      setHistoryEvents(events);
    } catch {
      message.error('無法載入審核歷史');
    } finally {
      setHistoryLoading(false);
    }
  }, [record]);

  // Legacy alias kept for backward compat
  const handleApprove = handleApproveClick;

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fd120_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d202Api.writeoff(record.fd120_01);
          message.success(resp.message || '沖銷成功');
          await loadRecordWithNames(record.fd120_01);
        } catch (err: unknown) {
          const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
          message.error(msg);
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  return {
    handleApprove,
    handleSubmitClick,
    handleApproveClick,
    handleRejectClick,
    handleWriteoff,
    handleShowHistory,
    historyVisible,
    setHistoryVisible,
    historyLoading,
    historyEvents,
  };
}
